import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { LayoutGrid, CheckSquare, Calendar as CalendarIcon, Users, MessageSquare, Search, LogOut, KeyRound, MoreHorizontal, LayoutTemplate, Mail, UsersRound, UserCircle2, Shield, Bell, Inbox, Sparkles, GitBranch, Building2 } from "lucide-react";
import GlobalSearch from "@/components/GlobalSearch";
import AssistantWidget from "@/components/AssistantWidget";
import QuickAddFAB from "@/components/QuickAddFAB";
import HQMark from "@/components/HQMark";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Restricted Members (any authenticated user whose platform_role is not
// "admin") see only navigation that maps to permission-safe, scoped
// pages: HQ home, their Tasks, their Calendar, their Contacts, Intake,
// and Search. Every other section is either admin-only in the backend
// (Team, Audit, Inbox), operates over data the Member can't read
// (Digest, Alerts, Templates, Organizations, Signals, Opportunities,
// Ask HQ, Executive Assistant), or duplicates a permission-denied
// dead-end. Hiding these is UX cleanup — backend authorization
// remains the source of truth, so direct URLs still return the
// existing denial behaviour.
const NAV = [
  { to: "/", label: "HQ", icon: LayoutGrid, testid: "nav-hq", restrictedShow: true },
  { to: "/tasks", label: "Tasks", icon: CheckSquare, testid: "nav-tasks", restrictedShow: true },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon, testid: "nav-calendar", restrictedShow: true },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts", restrictedShow: true },
  { to: "/templates", label: "Templates", icon: LayoutTemplate, testid: "nav-templates" },
  { to: "/digest", label: "Digest", icon: Mail, testid: "nav-digest" },
  { to: "/attention", label: "Alerts", icon: Bell, testid: "nav-attention" },
  { to: "/intake", label: "Intake", icon: Sparkles, testid: "nav-intake", restrictedShow: true },
  { to: "/organizations", label: "Organizations", icon: Building2, testid: "nav-organizations" },
  { to: "/signals", label: "Signals", icon: GitBranch, testid: "nav-signals" },
  { to: "/ask", label: "Ask HQ", icon: Sparkles, testid: "nav-ask-hq" },
  { to: "/opportunities", label: "Opportunities", icon: Sparkles, testid: "nav-opportunities" },
  { to: "/inbox", label: "Inbox", icon: Inbox, testid: "nav-inbox", adminOnly: true },
  { to: "/team", label: "Team", icon: UsersRound, testid: "nav-team", adminOnly: true },
  { to: "/audit", label: "Audit", icon: Shield, testid: "nav-audit", adminOnly: true },
  { to: "/assistant", label: "Assistant", icon: MessageSquare, testid: "nav-assistant" },
];

// A user is "restricted" when they authenticated but their
// platform_role is not admin. `role` is legacy fallback.
const isAdminUser = (u) => (u?.platform_role || u?.role) === "admin";

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [searchOpen, setSearchOpen] = useState(false);
  const isAdmin = isAdminUser(user);
  // For restricted (non-admin) members we render only the six allow-listed
  // sections; for admin/executive users the full nav is preserved unchanged.
  const visibleNav = NAV.filter((n) => {
    if (n.adminOnly) return isAdmin;
    if (!isAdmin) return !!n.restrictedShow;
    return true;
  });

  React.useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="flex h-screen bg-[#0B0E14] text-white overflow-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-60 flex-shrink-0 border-r border-white/10 bg-[#0B0E14] flex-col" data-testid="sidebar">
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <button onClick={() => nav("/")} className="flex items-center gap-2.5 text-white" data-testid="sidebar-logo">
            <HQMark size={32} />
            <div>
              <div className="font-display text-sm font-semibold leading-tight tracking-tight">Alias Distribution</div>
              <div className="text-[10px] uppercase tracking-widest text-white/40">HQ</div>
            </div>
          </button>
        </div>

        <nav className="flex-1 py-4 px-3 space-y-1">
          {visibleNav.map((n) => {
            const Icon = n.icon;
            const active = n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                  active ? "bg-white/10 text-white" : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-medium">{n.label === "Assistant" ? "Executive Assistant" : n.label}</span>
              </Link>
            );
          })}
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="nav-search"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-white/60 hover:text-white hover:bg-white/5"
          >
            <Search className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1 flex items-center justify-between text-sm font-medium">
              Search
              <kbd className="hidden lg:inline text-[10px] px-1.5 py-0.5 rounded border border-white/15 text-white/40">⌘K</kbd>
            </span>
          </button>
        </nav>

        <div className="p-3 border-t border-white/10">
          <Link
            to="/account"
            data-testid="sidebar-account-link"
            className={`flex items-center gap-2 px-2 py-2 rounded-md transition-colors ${
              loc.pathname.startsWith("/account") ? "bg-white/10" : "hover:bg-white/5"
            }`}
            title="Account"
          >
            <div className="w-8 h-8 rounded-full bg-white/10 border border-white/10 flex items-center justify-center text-xs font-semibold">
              {((user?.display_name || user?.name) || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium truncate">{user?.display_name || user?.name || "Admin"}</div>
              <div className="text-[10px] text-white/40 truncate">{user?.email}</div>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                  title="More"
                  onClick={(e) => e.preventDefault()}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" side="top" className="bg-[#151A23] border-white/10 text-white">
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="menu-account" className="hover:bg-white/10 focus:bg-white/10">
                  <UserCircle2 className="w-4 h-4 mr-2" /> Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings/password")} data-testid="menu-change-password" className="hover:bg-white/10 focus:bg-white/10">
                  <KeyRound className="w-4 h-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-400 hover:bg-red-500/10 focus:bg-red-500/10">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Link>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col h-full overflow-hidden relative">
        <header className="hidden md:flex h-16 border-b border-white/10 items-center px-6 md:px-8 justify-between bg-[#0B0E14]/80 backdrop-blur-md z-40">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-3 px-3.5 py-2 rounded-md bg-white/5 border border-white/10 hover:border-white/20 transition-colors text-sm text-white/50 w-full max-w-md"
            data-testid="header-search-btn"
          >
            <Search className="w-4 h-4" />
            <span>Search entities, projects, tasks, contacts, notes…</span>
            <kbd className="ml-auto text-[10px] px-1.5 py-0.5 rounded border border-white/15 text-white/40">⌘K</kbd>
          </button>
          <div className="flex items-center gap-3 ml-6">
            <span className="text-xs text-white/40 uppercase tracking-widest">Executive Command Center</span>
          </div>
        </header>

        {/* Mobile top bar */}
        <header className="md:hidden flex-shrink-0 border-b border-white/10 bg-[#0B0E14]/95 backdrop-blur-md z-40" style={{ paddingTop: "env(safe-area-inset-top)" }} data-testid="mobile-topbar">
          <div className="flex items-center gap-2 px-4 h-14">
            <button onClick={() => nav("/")} className="flex items-center gap-2 flex-shrink-0 text-white" data-testid="mobile-logo">
              <HQMark size={30} />
              <span className="font-display text-sm font-semibold tracking-tight">Alias HQ</span>
            </button>
            <button
              onClick={() => setSearchOpen(true)}
              className="flex-1 flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-white/10 text-xs text-white/50 min-w-0"
              data-testid="mobile-search-btn"
            >
              <Search className="w-3.5 h-3.5 flex-shrink-0" />
              <span className="truncate">Search</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="mobile-menu-btn" className="p-2 rounded-md text-white/60 hover:text-white hover:bg-white/10 flex-shrink-0">
                  <MoreHorizontal className="w-5 h-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#151A23] border-white/10 text-white">
                <div className="px-2 py-1.5 text-xs text-white/50">{user?.email}</div>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => nav("/account")} data-testid="mobile-menu-account" className="hover:bg-white/10 focus:bg-white/10">
                  <UserCircle2 className="w-4 h-4 mr-2" /> Account
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => nav("/settings/password")} data-testid="mobile-menu-change-password" className="hover:bg-white/10 focus:bg-white/10">
                  <KeyRound className="w-4 h-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={logout} data-testid="mobile-menu-logout" className="text-red-400 hover:bg-red-500/10 focus:bg-red-500/10">
                  <LogOut className="w-4 h-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-hidden pb-[calc(64px+env(safe-area-inset-bottom))] md:pb-0">{children}</main>

        {/* Mobile Bottom Nav */}
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0B0E14]/95 backdrop-blur-lg border-t border-white/10"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="mobile-bottom-nav"
        >
          <div className="flex items-stretch justify-around h-16">
            {visibleNav.map((n) => {
              const Icon = n.icon;
              const active = n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-${n.testid}`}
                  className={`flex-1 flex flex-col items-center justify-center gap-1 min-w-0 relative ${
                    active ? "text-white" : "text-white/50"
                  }`}
                >
                  {active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full bg-white" />}
                  <Icon className="w-[18px] h-[18px]" />
                  <span className="text-[10px] font-medium tracking-tight">{n.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      </div>

      <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
      {isAdmin && <AssistantWidget />}
      <QuickAddFAB />
    </div>
  );
}
